"use client";

import { JSX, useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import CloseIcon from "@/assets/icons/CloseIcon.svg";
import ExpandIcon from "@/assets/icons/ExpandIcon.svg";
import ShrinkIcon from "@/assets/icons/ShrinkIcon.svg";
import { Icon, IconProps } from "../Icon/Icon";
import Image from "next/image";
import { useMedia } from "@/lib/hooks/useMedia";

interface ModalProps {
  content: JSX.Element | JSX.Element[];
  toggleModal: () => void;
  title: string;
  titleIcon?: string;
  titleIconWidth?: number;
  titleIconHeight?: number;
  titleColorFilter?: boolean;
  showtimesIcon?: boolean;
  icons?: IconProps[];
  headerAction?: JSX.Element;
  needFullScreen?: boolean;
  modalWidth?: string;
  modalHeight?: string;
  modalBgSize?: string;
  comingSoon?: boolean;
  top?: string;
  useHeadNav?: boolean;
  modalIndex?: string;
  expandable?: boolean;
}

const ModalBg = styled.div<{
  $isMobile?: string;
  $fullScreen?: boolean;
  $modalWidth?: string;
  $modalHeight?: string;
  $modalBgSize?: string;
  $isComingSoon?: boolean;
  $top?: string;
  $useHeadNav?: boolean;
  $modalIndex?: string;
  $expanded?: boolean;
}>`
  position: fixed;
  left: 0;
  right: 0;
  top: ${(props) => (props.$top ? props.$top : !props.$isComingSoon ? 0 : "")};
  bottom: ${(props) => !props.$isComingSoon && 0};
  background: rgba(0, 0, 0, 0.2);
  width: 100%;
  height: ${(props) => (props.$modalBgSize ? props.$modalBgSize : "100%")};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${(props) =>
    props.$modalIndex ? props.$modalIndex : props.$isComingSoon ? "9" : "10"};

  &:focus-visible {
    outline: 0;
  }

  .modal {
    background-color: ${(props) => props.theme.white};
    border-radius: ${(props) => (props.$fullScreen ? "0px" : "20px")};
    z-index: 9995;
    box-shadow: ${(props) =>
      props.$fullScreen ? "" : "0 4px 16px RGBa(0, 0, 0, 0.4)"};
    transition:
      max-width 0.3s ease,
      width 0.3s ease,
      height 0.3s ease;
    max-width: ${(props) =>
      props.$fullScreen
        ? "unset"
        : props.$expanded
          ? "95vw"
          : props.$modalWidth
            ? props.$modalWidth
            : "70%"};

    width: ${(props) =>
      props.$fullScreen
        ? "100vw"
        : props.$expanded
          ? "95vw"
          : props.$modalWidth
            ? props.$modalWidth
            : "unset"};
    height: ${(props) =>
      props.$fullScreen
        ? "100dvh"
        : props.$expanded
          ? "95vh"
          : props.$modalHeight
            ? props.$modalHeight
            : "unset"};
    max-height: 100dvh;
    margin-bottom: ${(props) => props.$isComingSoon && "200px"};
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
    display: flex;
    flex-direction: column;
    min-height: 0;

    .icons {
      display: flex;
      gap: 25px;
    }

    .header {
      display: flex;
      border-radius: ${(props) =>
        props.$fullScreen ? "0px" : "20px 20px 0 0"};
      background-color: ${(props) => props.theme.main};
      justify-content: ${(props) =>
        props.$fullScreen ? "flex-end" : "space-between"};
      flex-direction: ${(props) => (props.$fullScreen ? "row-reverse" : "row")};
      align-items: center;
      margin-bottom: ${(props) => (props.$fullScreen ? "0px" : "10px")};
      padding: 10px 20px;
      padding-top: ${(props) => props.$fullScreen && "10px"};
      gap: 10px;

      .title-row {
        display: flex;
        align-items: center;
        gap: 15px;
        font-size: 36px;
        font-weight: bold;
        flex-wrap: wrap;
        padding-right: 30px;

        h4 {
          margin: 0;
          color: ${(props) => props.theme.white};
          line-height: normal;
        }
      }

      .close-icon {
        cursor: pointer;
      }

      .header-action {
        display: flex;
        align-items: center;
        margin-left: auto;
      }
    }
  }

  @media (max-width: 1024px) {
    z-index: ${(props) =>
      props.$modalIndex ? props.$modalIndex : props.$isComingSoon ? "2" : "10"};
  }

  // will need to update later
  @media (max-width: 768px) {
    animation: slideInFromLeft 0.4s ease-out;
    z-index: ${(props) =>
      props.$modalIndex
        ? props.$modalIndex
        : props.$useHeadNav || props.$isComingSoon
          ? "2"
          : "10"};

    @keyframes slideInFromLeft {
      from {
        transform: translateX(-100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }

    .modal {
      max-width: ${(props) => !props.$fullScreen && "90%"};
      border-radius: ${(props) => props.$fullScreen && "0px"};

      .header {
        border-radius: ${(props) => props.$fullScreen && "0px"};
        background-color: ${(props) =>
          props.$fullScreen ? props.theme.white : props.theme.main};
        align-items: left;
        border-bottom: 1px solid ${(props) => props.theme.main};

        .title-row {
          align-items: left;
          padding-right: ${(props) => props.$fullScreen && "0 !important"};

          h4 {
            font-size: ${(props) => (props.$fullScreen ? "1.25rem" : "26px")};
            color: ${(props) =>
              !props.$fullScreen ? props.theme.white : props.theme.main};
            font-weight: ${(props) =>
              props.$fullScreen ? "normal" : "revert"};
          }
        }

        p {
          margin-top: 0px;
        }
      }
    }
  }
`;

const ContentDiv = styled.div<{ $fullScreen?: boolean }>`
  padding: 0 20px 20px;
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;

  @media (max-width: 768px) {
    padding: ${(props) => (props.$fullScreen ? "0" : "0 10px 10px")};
    overscroll-behavior: contain;
  }
`;

const MobileClose = styled.div`
  position: absolute;
  height: 50px;
  width: 50px;
  background-color: transparent;
  left: 0;

  @media (max-width: 260px) {
    height: 80px;
  }
`;

export function Modal({
  title,
  titleIcon,
  titleIconHeight,
  titleIconWidth,
  titleColorFilter = true,
  content,
  toggleModal,
  showtimesIcon = false,
  icons = undefined,
  headerAction,
  needFullScreen = false,
  modalWidth,
  modalHeight,
  modalBgSize,
  comingSoon = false,
  top,
  useHeadNav,
  modalIndex,
  expandable = false,
}: ModalProps) {
  const KEY_NAME_ESC = "Escape";
  const KEY_EVENT_TYPE = "keyup";
  const max768w = useMedia("(max-width: 768px)");
  const [isExpanded, setIsExpanded] = useState(false);
  const showExpandToggle = expandable && !needFullScreen && !max768w;

  const handleEscKey = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === KEY_NAME_ESC) {
        toggleModal();
      }
    },
    [toggleModal]
  );

  useEffect(() => {
    document.addEventListener(KEY_EVENT_TYPE, handleEscKey, false);

    return () => {
      document.removeEventListener(KEY_EVENT_TYPE, handleEscKey, false);
    };
  }, [handleEscKey]);

  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, []);

  return (
    <ModalBg
      $fullScreen={needFullScreen}
      $modalWidth={modalWidth}
      $modalHeight={modalHeight}
      $modalBgSize={modalBgSize}
      $isComingSoon={comingSoon}
      $top={top}
      $useHeadNav={useHeadNav}
      $modalIndex={modalIndex}
      $expanded={isExpanded && showExpandToggle}
    >
      <div
        className="modal"
        role="button"
        tabIndex={0}
        data-expanded={isExpanded && showExpandToggle}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="header">
          {needFullScreen && headerAction && (
            <div className="header-action">{headerAction}</div>
          )}
          <div className="title-row">
            {needFullScreen && <MobileClose onClick={() => toggleModal()} />}
            {titleIcon && (
              <Image
                src={titleIcon}
                height={titleIconHeight ?? 20}
                alt="title-icon"
                width={titleIconWidth ?? 20}
                style={
                  titleColorFilter
                    ? {
                        filter: "brightness(0) invert(1)",
                      }
                    : {}
                }
              />
            )}
            {title && <h4 className="johnson-display">{title}</h4>}
          </div>
          <div className="icons">
            {icons &&
              icons.map((icon) => (
                <Icon
                  key={icon.url}
                  url={icon.url}
                  color={icon.color}
                  onPress={icon.onPress}
                  height={icon.height}
                  width={icon.width}
                />
              ))}
            {!needFullScreen && headerAction}
            {showExpandToggle && (
              <Icon
                color="#fff"
                url={isExpanded ? ShrinkIcon.src : ExpandIcon.src}
                width="30px"
                height="30px"
                onPress={() => setIsExpanded((prev) => !prev)}
              />
            )}
            {showtimesIcon && (
              <Icon
                color={needFullScreen ? "#EB1700" : "#fff"}
                url={CloseIcon.src}
                width={max768w ? "20px" : "30px"}
                height={max768w ? "20px" : "30px"}
                onPress={() => toggleModal()}
              />
            )}
          </div>
        </div>
        <ContentDiv $fullScreen={needFullScreen}>{content}</ContentDiv>
      </div>
    </ModalBg>
  );
}
